import { useEffect, useMemo } from 'react';
import { WidgetProps } from '@rjsf/utils';
import { Autocomplete, Checkbox, TextField, Tooltip } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import LockIcon from '@mui/icons-material/Lock';
import { CreateWorkflowNode } from 'api/model';
import { ToolChip } from './MultiSelectWidget.styles';

type MultiSelectOption = { value: string; label: string };

type ValuesOption = string[] | ((formData: CreateWorkflowNode | undefined) => string[] | undefined);
type TextOption = string | ((formData: CreateWorkflowNode | undefined) => string | undefined);

type MultiSelectWidgetOptions = {
  placeholder?: string;
  lockedValues?: ValuesOption;
  blockedValues?: ValuesOption;
  noticeText?: TextOption;
};

const resolveValues = (option: ValuesOption | undefined, formData: CreateWorkflowNode | undefined): string[] =>
  (typeof option === 'function' ? option(formData) : option) ?? [];

const resolveText = (option: TextOption | undefined, formData: CreateWorkflowNode | undefined): string | undefined =>
  typeof option === 'function' ? option(formData) : option;

function MultiSelectWidget(props: WidgetProps) {
  const { value, onChange, schema, required, disabled, readonly, label } = props;
  const widgetOptions = props.options as MultiSelectWidgetOptions | undefined;
  const formData = props.formContext?.formData as CreateWorkflowNode | undefined;

  const enumOptions = (props.options?.enumOptions ?? []) as MultiSelectOption[];

  const lockedValues = resolveValues(widgetOptions?.lockedValues, formData);
  const blockedValues = resolveValues(widgetOptions?.blockedValues, formData);
  const noticeText = resolveText(widgetOptions?.noticeText, formData);

  const normalize = (values: string[]): string[] =>
    Array.from(new Set([...values, ...lockedValues])).filter((v) => !blockedValues.includes(v));

  const selectedValues = useMemo(
    () => normalize(Array.isArray(value) ? value : []),
    [JSON.stringify(value), lockedValues.join(','), blockedValues.join(',')],
  );

  useEffect(() => {
    const current = Array.isArray(value) ? value : [];
    if (JSON.stringify(selectedValues) !== JSON.stringify(current)) {
      onChange(selectedValues);
    }
  }, [selectedValues.join(',')]);

  const selectedOptions = enumOptions.filter((option) => selectedValues.includes(option.value));

  const fieldLabel = schema.title ?? label;
  const placeholder = widgetOptions?.placeholder ?? 'Select options';

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      disabled={disabled || readonly}
      options={enumOptions}
      value={selectedOptions}
      getOptionDisabled={(option) => lockedValues.includes(option.value) || blockedValues.includes(option.value)}
      isOptionEqualToValue={(option, selected) => option.value === selected.value}
      onChange={(_, newValue) => onChange(normalize(newValue.map((option) => option.value)))}
      renderOption={(optionProps, option, { selected }) => {
        const locked = lockedValues.includes(option.value);
        const blocked = blockedValues.includes(option.value);
        return (
          <li {...optionProps} key={option.value}>
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              checked={selected || locked}
            />
            {option.label}
            {(locked || blocked) && noticeText && (
              <Tooltip title={noticeText}>
                <LockIcon fontSize="small" color="disabled" className="ml-1 pointer-events-auto" />
              </Tooltip>
            )}
          </li>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, onDelete, ...tagProps } = getTagProps({ index });
          const locked = lockedValues.includes(option.value);
          const chip = (
            <ToolChip
              label={option.label}
              size="small"
              key={option.value ?? key}
              locked={locked}
              icon={locked ? <LockIcon /> : undefined}
              onDelete={locked ? undefined : onDelete}
              {...tagProps}
            />
          );
          return locked && noticeText ? (
            <Tooltip title={noticeText} key={option.value ?? key}>
              <span>{chip}</span>
            </Tooltip>
          ) : (
            chip
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={fieldLabel}
          required={required && selectedValues.length === 0}
          placeholder={selectedValues.length === 0 ? placeholder : ''}
          helperText={noticeText}
        />
      )}
      fullWidth
      sx={(theme) => ({
        '& .MuiInputBase-root': {
          minHeight: theme.spacing(5.5),
          paddingTop: theme.spacing(0.5),
          paddingBottom: theme.spacing(0.5),
        },
        '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
          paddingTop: 0,
          paddingBottom: 0,
        },
      })}
    />
  );
}

export default MultiSelectWidget;
